import {
  Text,
  Checkbox,
  Box,
  Textarea,
  Tooltip,
  Tr,
  Td,
  Flex,
  Button,
} from '@chakra-ui/react';
import {
  Column,
  ColumnFiltersState,
  Row,
  Table as ReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useState, useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { VerificationEligibilityReceivableGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  allReceivableEligibilityDifferentSelectActionsOptions,
  createInitialColumnVisibility,
  filterMenuZIndex,
  ReceivableEligibilityDifferencesSelectActions,
  receivableEligibilityDifferencesSelectActionToVerbMap,
  selectActionRowStickyColumnStyle,
  toggleCheckboxDataForAllRows,
  toggleCheckboxDataForRow,
  updateNotesDataForRow,
  selectModeFixedColumnsStyleToColumnIndexMap,
} from './VerificationReceivableEligibilityTableUtils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  IsolatedMultiSelect,
  IsolatedMultiSelectWithPortalIconButton,
} from 'packages/common-frontend/components/MultiSelect';
import { Select } from 'packages/common-frontend/components/Select';
import { FiFilter } from 'react-icons/fi';

type VerificationReceivableEligibilityColumnSelectorProps = {
  tableInstance: ReactTable<VerificationEligibilityReceivableGQLType>;
  verificationEligibilityReceivables: VerificationEligibilityReceivableGQLType[];
};
export const VerificationReceivableEligibilityColumnSelector = ({
  tableInstance,
  verificationEligibilityReceivables,
}: VerificationReceivableEligibilityColumnSelectorProps) => {
  const onApplyOptions = (selectedOptions: string[]) => {
    const updatedVisibleColumns = _.cloneDeep(
      createInitialColumnVisibility({ verificationEligibilityReceivables }),
    );

    _.forEach(selectedOptions, (option) => {
      updatedVisibleColumns[option] = true;
    });
    tableInstance.setColumnVisibility(updatedVisibleColumns);
  };

  const options = _.map(
    _.first(verificationEligibilityReceivables)?.baseFields,
    (_baseFieldValue, baseFieldKey) => {
      return {
        label: commonUtils.toSentenceCase(baseFieldKey),
        value: baseFieldKey,
      };
    },
  );

  const initialSelections = _.compact(
    _.map(tableInstance.getAllLeafColumns(), (column) => {
      return column.getIsVisible() ? column.id : null;
    }),
  );

  return (
    <Box w="228px" h="44px">
      <IsolatedMultiSelect
        applyButtonText="Display selected columns"
        buttonProps={{
          minWidth: '300px',
        }}
        counterLabel="Columns"
        initialSelections={initialSelections}
        isSearchable
        menuListProps={{ zIndex: 'dropdown' }}
        onApplyOptions={onApplyOptions}
        options={options}
        optionsHeader="Base fields"
        placeholderText="Select base fields"
        showSelectedCount
        titleText="Show/Hide Columns"
      />
    </Box>
  );
};

export const renderSelectCell = ({
  row,
  isDisabled,
}: {
  row: Row<VerificationEligibilityReceivableGQLType>;
  isDisabled: boolean;
}) => {
  return (
    <Box textAlign="center">
      <Checkbox
        isChecked={row.getIsSelected()}
        isDisabled={!row.getCanSelect() || isDisabled}
        onChange={row.getToggleSelectedHandler()}
      />
    </Box>
  );
};

export const renderWaivedIconCell = ({
  column,
  row,
}: {
  column: Column<VerificationEligibilityReceivableGQLType, any>;
  row: Row<VerificationEligibilityReceivableGQLType>;
}) => {
  const value = row.getValue(column.id);
  return (
    <Box textAlign="center">
      {value ? (
        <Tooltip label="Receivable waived">
          <span>
            <Icon icon="ban" color={colors.info} />
          </span>
        </Tooltip>
      ) : (
        '-'
      )}
    </Box>
  );
};

// currently unused
export const renderCheckboxHeaderCell = ({
  tableInstance,
  column,
  setData,
  currentData,
  isDisabled,
}: {
  tableInstance: ReactTable<VerificationEligibilityReceivableGQLType>;
  column: Column<VerificationEligibilityReceivableGQLType, any>;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
  isDisabled: boolean;
}) => (
  <Box textAlign="center">
    <Text mb="4px">Waive</Text>
    <Checkbox
      isChecked={tableInstance.getIsAllRowsSelected()}
      isIndeterminate={tableInstance.getIsSomeRowsSelected()}
      isDisabled={isDisabled}
      onChange={(event) => {
        toggleCheckboxDataForAllRows({
          columnId: column.id,
          currentValue: tableInstance.getIsAllRowsSelected(),
          setData,
          currentData,
        });
        tableInstance.getToggleAllRowsSelectedHandler()(event);
      }}
    />
  </Box>
);

// currently unused
export const renderCheckboxCell = ({
  column,
  row,
  currentValue,
  setData,
  currentData,
  isDisabled,
}: {
  column: Column<VerificationEligibilityReceivableGQLType, any>;
  row: Row<VerificationEligibilityReceivableGQLType>;
  currentValue: boolean;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
  isDisabled: boolean;
}) => {
  return (
    <Box textAlign="center">
      <Checkbox
        isChecked={row.getValue(column.id)}
        isDisabled={!row.getCanSelect() || isDisabled}
        onChange={() => {
          toggleCheckboxDataForRow({
            rowIndex: row.index,
            columnId: column.id,
            currentValue,
            setData,
            currentData,
          });
        }}
      />
    </Box>
  );
};

// currently unused
export const NotesCell = ({
  column,
  row,
  oldValue,
  setData,
  currentData,
  isDisabled,
}: {
  column: Column<VerificationEligibilityReceivableGQLType, any>;
  row: Row<VerificationEligibilityReceivableGQLType>;
  oldValue: string;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
  isDisabled: boolean;
}) => {
  const [notes, setNotes] = useState(oldValue ?? '');

  return (
    <Textarea
      value={notes}
      onChange={(event) => {
        setNotes(event.target.value);
      }}
      onBlur={() => {
        updateNotesDataForRow({
          rowIndex: row.index,
          columnId: column.id,
          newValue: notes,
          setData,
          currentData,
        });
      }}
      disabled={isDisabled}
    />
  );
};

// currently unused
export const renderNotesCell = ({
  column,
  row,
  oldValue,
  setData,
  currentData,
  isDisabled,
}: {
  column: Column<VerificationEligibilityReceivableGQLType, any>;
  row: Row<VerificationEligibilityReceivableGQLType>;
  oldValue: string;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
  isDisabled: boolean;
}) => (
  <NotesCell
    column={column}
    row={row}
    setData={setData}
    oldValue={oldValue}
    currentData={currentData}
    isDisabled={isDisabled}
  />
);

export const ColumnFilter = ({
  column,
  currentColumnFilters,
}: {
  column: Column<any, unknown>;
  currentColumnFilters: ColumnFiltersState;
}) => {
  const sortedUniqueValues = useMemo(
    () => Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column],
  );
  const options = useMemo(
    () =>
      _.map(sortedUniqueValues, (value) => ({
        label: commonUtils.toTitleCase(value),
        value,
      })),
    [sortedUniqueValues],
  );

  const onApplyOptions = (values: string[]) => {
    column.setFilterValue(values);
  };

  let initialSelections = sortedUniqueValues;
  if (currentColumnFilters.length) {
    initialSelections = _.chain(currentColumnFilters)
      .filter(({ id }) => id === column.id)
      .map(({ value }) => _.toString(value))
      .value();
  }

  return (
    <IsolatedMultiSelectWithPortalIconButton
      icon={<FiFilter />}
      options={options}
      onApplyOptions={onApplyOptions}
      initialSelections={initialSelections}
      titleText="Filter"
      applyButtonText="Apply filters"
      optionsHeader={commonUtils.toTitleCase(column.id)}
      zIndex={filterMenuZIndex}
    />
  );
};

type ReceivableEligibilitySelectActionRowProps = {
  selectedAction: ReceivableEligibilityDifferencesSelectActions;
  setSelectedAction: React.Dispatch<
    React.SetStateAction<ReceivableEligibilityDifferencesSelectActions>
  >;
  onExitSelectMode: () => void;
  colSpan: number;
  onOpenSelectActionDrawer: () => void;
  areReceivableIdsSelected: boolean;
};
export const ReceivableEligibilitySelectActionRow = ({
  selectedAction,
  onExitSelectMode,
  setSelectedAction,
  colSpan,
  onOpenSelectActionDrawer,
  areReceivableIdsSelected,
}: ReceivableEligibilitySelectActionRowProps) => {
  return (
    <Tr backgroundColor={colors.gray[100]}>
      <Td
        colSpan={selectModeFixedColumnsStyleToColumnIndexMap.length}
        padding="16px"
        position="sticky"
        style={selectActionRowStickyColumnStyle}
      >
        <Flex alignItems="center" fontSize="14px" fontWeight="500">
          <Text>Action</Text>
          <Box w="300px" ml="16px">
            <Select<ReceivableEligibilityDifferencesSelectActions>
              value={selectedAction}
              onChange={(
                newSelectedAction:
                  | ReceivableEligibilityDifferencesSelectActions
                  | undefined,
              ) => {
                if (newSelectedAction) {
                  setSelectedAction(newSelectedAction);
                }
              }}
              options={allReceivableEligibilityDifferentSelectActionsOptions}
              placeholderLabel="Select Action"
              buttonProps={{
                backgroundColor: 'white',
                fontSize: '14px',
              }}
            />
          </Box>
          <Button
            ml="32px"
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            backgroundColor="white"
            onClick={onExitSelectMode}
            fontSize="14px"
          >
            <Text>Cancel</Text>
          </Button>
          <Button
            ml="32px"
            colorScheme="aurora"
            fontSize="14px"
            onClick={onOpenSelectActionDrawer}
            isDisabled={!areReceivableIdsSelected}
          >
            <Text mr="8px">
              {commonUtils.toTitleCase(
                receivableEligibilityDifferencesSelectActionToVerbMap[
                  selectedAction
                ],
              )}
            </Text>
            <Icon icon="chevron-right" />
          </Button>
        </Flex>
      </Td>
      {/* Fill in the rest of the Actions row with blank cell of the same style */}
      <Td colSpan={colSpan} />
    </Tr>
  );
};
